/**
 * Keycloak React provider.
 *
 * - `init({ onLoad: 'check-sso' })` runs once and decides whether the user
 *   is already authenticated (silent SSO).
 * - Token refresh runs every minute; tokens are good for 15 minutes by default.
 * - The `useAuth()` hook is the only place application code reads identity.
 * - `setTokenProvider(...)` plugs the latest access token into the API client.
 *
 * Spec rule (S02 §2.6) — permissions ALWAYS come from JWT claims. Never read
 * from localStorage / Zustand / URL params. This provider exposes the
 * derived claims via `AuthenticatedUser` and that's the only allowed source.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';

import { keycloak, initKeycloak } from '@/lib/auth/keycloak';
import { setTokenProvider } from '@/lib/api';
import type { AuthenticatedUser, ExternalClientTier, UserRole } from '@/types/auth';

interface AuthContextValue {
  initialised: boolean;
  isAuthenticated: boolean;
  user: AuthenticatedUser | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const REFRESH_INTERVAL_MS = 60_000;
const REFRESH_MIN_VALIDITY_SECONDS = 70;

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [initialised, setInitialised] = useState(false);
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const tickRef = useRef<number | null>(null);

  // The API client pulls the token via this callback every request.
  // Register it once on mount so the very first call after init() succeeds.
  useEffect(() => {
    setTokenProvider(() => keycloak.token ?? null);
  }, []);

  // ── Init ──────────────────────────────────────────────────────
  useEffect(() => {
    let mounted = true;
    initKeycloak({
      onLoad: 'check-sso',
      silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
      pkceMethod: 'S256',
      checkLoginIframe: false,
    })
      .then((authenticated) => {
        if (!mounted) return;
        setInitialised(true);
        if (authenticated) {
          setUser(userFromKeycloak());
        }
      })
      .catch(() => {
        if (!mounted) return;
        setInitialised(true);
        setUser(null);
      });
    return () => {
      mounted = false;
    };
  }, []);

  // ── Periodic token refresh ───────────────────────────────────
  useEffect(() => {
    if (!user) return;
    tickRef.current = window.setInterval(() => {
      keycloak
        .updateToken(REFRESH_MIN_VALIDITY_SECONDS)
        .then((refreshed) => {
          if (refreshed) {
            setUser(userFromKeycloak());
          }
        })
        .catch(() => {
          setUser(null);
        });
    }, REFRESH_INTERVAL_MS);
    return () => {
      if (tickRef.current !== null) window.clearInterval(tickRef.current);
    };
  }, [user]);

  // ── Actions ──────────────────────────────────────────────────
  const login = useCallback(async () => {
    await keycloak.login({ redirectUri: window.location.href });
  }, []);

  const logout = useCallback(async () => {
    setUser(null);
    await keycloak.logout({ redirectUri: window.location.origin });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      initialised,
      isAuthenticated: Boolean(user),
      user,
      login,
      logout,
    }),
    [initialised, user, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ─── Hooks ─────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export function useCurrentUser(): AuthenticatedUser | null {
  return useAuth().user;
}

export function useHasRole(...roles: UserRole[]): boolean {
  const user = useCurrentUser();
  return user ? user.roles.some((r) => roles.includes(r)) : false;
}

export function useIsExternalClient(): boolean {
  const user = useCurrentUser();
  return user ? user.roles.includes('external_client') : false;
}

export function useIsInternalUser(): boolean {
  const user = useCurrentUser();
  return user ? !user.roles.includes('external_client') : false;
}

export function useTier(): ExternalClientTier | undefined {
  return useCurrentUser()?.tier;
}

// ─── Helpers ───────────────────────────────────────────────────

function userFromKeycloak(): AuthenticatedUser | null {
  const token = keycloak.tokenParsed as
    | (Record<string, unknown> & {
        realm_access?: { roles?: string[] };
        product_owner?: boolean;
        topic_domains?: string[];
        tier?: string;
        sub?: string;
        email?: string;
        name?: string;
      })
    | undefined;
  if (!token || !token.sub) return null;
  const roles = (token.realm_access?.roles ?? []) as string[];
  return {
    id: String(token.sub),
    email: String(token.email ?? ''),
    name: String(token.name ?? token.email ?? 'User'),
    roles: roles.filter(isUserRole),
    productOwner: Boolean(token.product_owner),
    tier: token.tier ? (token.tier as ExternalClientTier) : undefined,
    topicDomains: token.topic_domains ?? [],
  };
}

function isUserRole(r: string): r is UserRole {
  return (
    r === 'data_analyst_standard' ||
    r === 'data_analyst_domain_expert' ||
    r === 'internal_front_office' ||
    r === 'administrator' ||
    r === 'external_client'
  );
}
